import React, { type ReactNode, useEffect, useRef } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

export type AITelepointerStyles = 'DEFAULT' | 'ROVO';

export type AITelepointerWrapperProps = {
	children: ReactNode;
	hideTelepointer?: boolean;
	variant?: AITelepointerStyles;
};

const aiTelepointerStyles = xcss({
	display: 'inline-block',
	position: 'absolute',
	width: '1.5px',
	height: '25px',
	background: 'linear-gradient(45deg, #F8E6A0 -12.02%, #8BDBE5 19.18%, #0C66E4 71.87%)',
	marginLeft: 'space.025',
	marginTop: 'space.negative.100',
	'::after': {
		content: '"AI"',
		position: 'absolute',
		display: 'block',
		top: 'space.0',
		left: 'space.0',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '10px',
		fontWeight: 'font.weight.bold',
		width: '12.5px',
		height: '13px',
		paddingTop: 'space.025',
		paddingLeft: 'space.025',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 'initial',
		borderRadius: `0px ${token('radius.xsmall')} ${token('radius.xsmall')} 0px`,
		color: 'color.text.inverse',
		background: 'linear-gradient(45deg, #8BDBE5 -57%, #0C66E4 71.87%)',
	},
});

const rovoTelepointerStyles = xcss({
	display: 'inline-block',
	position: 'absolute',
	width: '1px',
	height: '25px',
	background: token('color.background.brand.boldest'),
	marginLeft: 'space.025',
	marginTop: 'space.negative.100',
	'::after': {
		content: '"Rovo"',
		position: 'absolute',
		display: 'block',
		top: 'space.0',
		left: 'space.0',
		font: 'font.body.small',
		fontWeight: 'font.weight.semibold',
		width: '32px',
		height: '13px',
		paddingBottom: 'space.025',
		paddingLeft: 'space.050',
		paddingRight: 'space.0',
		borderRadius: `0px ${token('radius.small')} ${token('radius.small')} 0px`,
		color: 'color.text.inverse',
		background: token('color.background.brand.boldest'),
		border: '1px solid transparent',
		backgroundImage: `linear-gradient(${token('color.background.brand.boldest')}, ${token('color.background.brand.boldest')}), conic-gradient(from 275deg at 50% 50%, #AF59E0 0deg, #AF59E0 140deg, #FCA700 140deg, #FCA700 185deg, #6A9A23 185deg, #6A9A23 310deg, #1868DB 310deg, #1868DB 360deg)`,
		backgroundOrigin: 'border-box',
		backgroundClip: 'padding-box, border-box',
	},
});

const isAValidContainer = (node: ChildNode): boolean => {
	if (node.hasChildNodes() && node.lastChild?.nodeType !== Node.TEXT_NODE) {
		return true;
	}
	return false;
};

const getNestedLastChild = (originNode: ChildNode): Node => {
	if (isAValidContainer(originNode) && originNode.lastChild) {
		return getNestedLastChild(originNode.lastChild);
	} else if (originNode.textContent === '' && originNode.parentNode) {
		return originNode.parentNode;
	} else {
		return originNode;
	}
};

export const AITelepointerWrapper = ({
	variant = 'DEFAULT',
	hideTelepointer = false,
	children,
}: AITelepointerWrapperProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const telepointerRef = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		if (!hideTelepointer) {
			const renderer = containerRef.current!;

			if (renderer.lastChild && telepointerRef.current) {
				getNestedLastChild(renderer.lastChild).appendChild(telepointerRef.current);
			}
		}
	}, [children, hideTelepointer]);

	if (hideTelepointer) {
		return <React.Fragment>{children}</React.Fragment>;
	}

	return (
		<Box ref={containerRef}>
			<Box
				as="span"
				ref={telepointerRef}
				xcss={variant === 'ROVO' ? rovoTelepointerStyles : aiTelepointerStyles}
				testId="ai-telepointer-cursor"
			/>
			{children}
		</Box>
	);
};
